import type { ForecastEvent } from "./model.js";

/**
 * Stable cross-vintage identity for timeline trigger events.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * A trigger event carries TWO identities that cannot share one string:
 *
 *   `id`            — the name THIS VINTAGE'S RUN gave the trigger (`e1`, `h3`).
 *                     Byte-identical to what the forecast server emitted, so it is
 *                     the join key back to run artifacts: the SSE
 *                     `future_trigger_upserted` frames, the GCS envelope, the
 *                     harness `result.json`, and the report payload the
 *                     derived-branch handler reads. Unique WITHIN a vintage;
 *                     meaningless across vintages (every run restarts at `e1`).
 *
 *   `occurrenceKey` — the same REAL-WORLD OCCURRENCE, across vintages. The handle
 *                     for anything that must survive a reforecast: React DOM keys,
 *                     the slide-anchor cache, collapse/promote sets, dedup, and
 *                     live→done reconciliation.
 *
 * `mergeReforecastEvents` used to serve the second by overwriting the first,
 * rewriting every merged event's `id` to `ev:<title>::<date>`. That is what broke
 * the derived branch: the persisted row said
 * `ev:broad intense flu activity in early february::2027-02-13` while the report
 * payload behind the same pointer said `e6`, so the server's lookup found nothing
 * and 409'd `source_changed`. Splitting the fields lets each keep its own contract.
 *
 * ── Why the key is content-derived and not contract-derived ─────────────────
 * The stronger identity would be the trigger's CONTRACT (question +
 * resolution_criteria + resolution_time), which is what `sourceEventIdentity`
 * already computes for derived-branch validation. It cannot be used here:
 * `projectLiveEvents` builds a live node from a `future_trigger_upserted` frame
 * that carries none of those fields (only id/title/date/description/source). A
 * contract-derived key would therefore differ between the live node and the landed
 * event for the SAME trigger, and `done` would remount every node — which is
 * exactly the defect this split exists to prevent, arriving from the other side.
 *
 * So the key is title+date, like the id it replaces, with the two things that
 * actually made the old scheme wrong fixed: it normalizes properly (below), and it
 * is no longer pretending to be a run reference.
 */

/**
 * Characters kept as SEMANTIC during normalization, beyond letters/digits/space.
 *
 * Comparison and arithmetic signs are load-bearing in trigger titles — "CPI > 3%"
 * and "CPI < 3%" are different occurrences on the same date, and folding them
 * together would silently merge two triggers into one node. `!` and `~` are in the
 * set for the same reason and only for it: they are the second character of `!=`
 * and `~=`, so dropping them would make "CPI != 3%" fold onto "CPI = 3%" and the
 * content dedup would discard one of two genuinely different occurrences.
 * Everything outside this set is decorative (degree signs, quotes, brackets,
 * commas) and is dropped, which is what makes `+2.7°c` and `+2.7c` converge.
 */
const SEMANTIC_PUNCTUATION = "<>=!~+\\-%/.#&";
const DECORATIVE = new RegExp(`[^\\p{L}\\p{N}\\s${SEMANTIC_PUNCTUATION}]`, "gu");
const COMBINING_MARKS = /\p{M}/gu;
const WHITESPACE_RUN = /\s+/g;

/**
 * Fold a trigger title to its comparable form.
 *
 * NFKD + combining-mark strip folds `niño` → `nino` and `²` → `2`; dropping
 * decorative characters folds `+2.7°c` → `+2.7c`. Both were needed: a single row
 * was observed holding `ev:niño 3.4 index reaches +2.7°c::2026-08-12` AND
 * `ev:nino 3.4 index reaches +2.7c::2026-08-12` — one occurrence admitted twice,
 * because the old key only did `.toLowerCase().trim()`.
 */
export function normalizeTitle(title: unknown): string {
  if (typeof title !== "string") return "";
  return title
    .normalize("NFKD")
    .replace(COMBINING_MARKS, "")
    .replace(DECORATIVE, "")
    .replace(WHITESPACE_RUN, " ")
    .trim()
    .toLowerCase();
}

/**
 * The occurrence key for one event, or null when it cannot be derived.
 *
 * Uses `date` (the START), not `endDateOf`, matching the key the old `contentId`
 * built — so a legacy row backfilled on hydrate lands on the same key a fresh run
 * produces for the same occurrence, and collapse/promote state survives the
 * upgrade. Returns null for an event with no usable title+date; the caller falls
 * back to the event's own id.
 */
export function occurrenceKeyOf(event: ForecastEvent): string | null {
  const title = normalizeTitle(event?.title);
  const date = typeof event?.date === "string" ? event.date.trim() : "";
  if (!title || !date) return null;
  return `occ:${title}::${date}`;
}

/**
 * Stamp `occurrenceKey` onto every event in an array, in place, idempotently.
 *
 * IDEMPOTENT BY DESIGN: an event that already carries a key keeps it untouched.
 * Stamping runs at several ingest points (`filterAndStampEvents`, hydration, and
 * the reforecast merge) over overlapping arrays, and a key that changed on a
 * re-stamp would remount the node it exists to hold still.
 *
 * Collision suffixes are assigned only to events that arrive WITHOUT a key, so an
 * already-keyed event never has its key shifted by a newcomer. Two genuinely
 * distinct events that normalize to the same title+date still need distinct React
 * keys, hence the `#n` disambiguation — the same scheme the old `contentId` used.
 *
 * Mutates and returns the array (matching `filterAndStampEvents`, which already
 * stamps `isPast` in place).
 */
export function stampOccurrenceKeys(events: ForecastEvent[]): ForecastEvent[] {
  const used = new Set<string>();
  for (const event of events) {
    const existing = event?.occurrenceKey;
    if (typeof existing === "string" && existing) used.add(existing);
  }
  for (const event of events) {
    if (!event || typeof event !== "object") continue;
    const existing = event.occurrenceKey;
    if (typeof existing === "string" && existing) continue;
    const base =
      occurrenceKeyOf(event) ??
      (event.id != null && event.id !== "" ? `occ-id:${String(event.id)}` : "occ:unkeyed");
    let key = base;
    let n = 1;
    while (used.has(key)) key = `${base}#${++n}`;
    used.add(key);
    event.occurrenceKey = key;
  }
  return events;
}

/**
 * Read an event's stable handle, falling back to its id.
 *
 * The fallback keeps render sites total: an event that reached a component without
 * passing an ingest point (a hand-built fixture, a tour event) still renders under
 * a usable key rather than `undefined`, which React would collapse across siblings.
 */
export function eventKey(event: ForecastEvent | null | undefined): string {
  const key = event?.occurrenceKey;
  if (typeof key === "string" && key) return key;
  const id = event?.id;
  return id != null ? String(id) : "";
}

/**
 * Encode arbitrary text into the `[A-Za-z0-9_-]` fragment a DOM id may contain,
 * WITHOUT losing distinctions.
 *
 * `_` is the escape introducer, so it is escaped too (as its own code point) — a
 * literal underscore passing through unencoded could otherwise alias the encoding
 * of some other character. That makes the scheme prefix-free and therefore
 * injective by construction: every escape is `_<base36 code point>_`, base36 digits
 * never contain `_`, and no raw character outside `[A-Za-z0-9-]` reaches the output.
 *
 * The `u` flag matters: without it an astral character is matched as two lone
 * surrogates and `codePointAt(0)` on each would encode the wrong scalar.
 */
function escapeDomIdFragment(raw: string): string {
  return raw.replace(/[^A-Za-z0-9-]/gu, (ch) => `_${ch.codePointAt(0)!.toString(36)}_`);
}

/**
 * The shared id stem for one event's SVG gradients on one timeline row.
 *
 * Callers prefix it (`span-`, `bell-`, `conn-`) and reference the result through
 * `url(#…)`, so it must be BOTH a legal id fragment and unique per event — a
 * duplicate stem means two `<linearGradient>` elements share an id and `url(#…)`
 * silently resolves to whichever the document defined first, painting one trigger
 * with another's gradient.
 *
 * The previous encoding collapsed every non-alphanumeric to a single `_`, which is
 * lossy in exactly the place `occurrenceKey` is deliberately precise: the key keeps
 * comparison operators semantic (see SEMANTIC_PUNCTUATION), so "CPI > 3%" and
 * "CPI = 3%" on one date are distinct occurrences — and both collapsed to
 * `occ_cpi___3___<date>`. Escaping injectively keeps distinct keys distinct.
 */
export function eventGradientIdBase(
  rowId: string,
  event: ForecastEvent | null | undefined,
): string {
  return escapeDomIdFragment(`${rowId}-${eventKey(event)}`);
}
